import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { getLicenseUsage, type LicenseUsage } from "../../api/licenses.ts";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { getPagePath } from "../../pages/pageRegistry.ts";
import styles from "./licenseCounter.module.scss";

/**
 * Top-bar paid-licence counter: `Licenses <used> / <total>`, summed over every licence
 * type, with a per-type breakdown on hover/focus.
 *
 * `used` is the weighted cost the LICENCED batchjobs charge — one licence per kiosk plus
 * one per IQBox in a store kiosk (`Kiosk_License_Weight_FN`), excluding non-LIVE kiosks
 * that have stopped reporting. It is deliberately uncapped, so an over-deployed endpoint
 * shows e.g. `17 / 15` in the warn colour rather than a reassuring `15 / 15`.
 *
 * Renders nothing unless the `HeaderShowLicenses` DB feature flag is on AND the partner
 * actually has S_Licensed_Kiosks rows (server returns an empty list otherwise).
 */

/** Client-side hold so a reload doesn't refetch; server caches 10 min anyway. */
const CACHE_TTL_MS = 10 * 60 * 1000;

function cacheKey(endpointId: number | undefined) {
    return `licenseUsage:${endpointId ?? 0}`;
}

function readCached(endpointId: number | undefined): LicenseUsage[] | null {
    try {
        const raw = sessionStorage.getItem(cacheKey(endpointId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { t: number; rows: LicenseUsage[] };
        if (!parsed || !Array.isArray(parsed.rows)) return null;
        if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
        return parsed.rows;
    } catch {
        return null;
    }
}

function writeCached(endpointId: number | undefined, rows: LicenseUsage[]) {
    try {
        sessionStorage.setItem(cacheKey(endpointId), JSON.stringify({ t: Date.now(), rows }));
    } catch {
        // Storage full / disabled — the counter just refetches next reload
    }
}

export function LicenseCounter() {
    const { cache, canRead, session } = useAuth();
    const endpointId = session?.ActiveEndPointID;
    const enabled = !!getDbFeatureFlags(cache).HeaderShowLicenses;

    const [rows, setRows] = useState<LicenseUsage[] | null>(() => (enabled ? readCached(endpointId) : null));
    const bootedRef = useRef(false);

    useEffect(() => {
        if (!enabled || bootedRef.current) return;
        bootedRef.current = true;
        if (readCached(endpointId)) return;
        getLicenseUsage().then((res) => {
            if (res.bError || !res.oData) return;
            setRows(res.oData);
            writeCached(endpointId, res.oData);
        }).catch(() => {
            // Counter is decorative — a failed fetch just leaves it hidden
        });
    }, [enabled, endpointId]);

    const canOpenPage = useMemo(() => {
        const page = cache?.PageInfo?.Page?.find(p => p.Name?.toLowerCase() === "licensepage");
        return page ? canRead(page.RowID) : false;
    }, [cache, canRead]);

    const totals = useMemo(() => {
        if (!rows?.length) return null;
        const used = rows.reduce((sum, r) => sum + (r.UsedWeight || 0), 0);
        const unlimited = rows.some(r => r.LicensedCount == null);
        const total = rows.reduce((sum, r) => sum + (r.LicensedCount ?? 0), 0);
        return { used, total, unlimited, over: !unlimited && used > total };
    }, [rows]);

    if (!enabled || !rows?.length || !totals) return null;

    const totalLabel = totals.unlimited ? "∞" : String(totals.total);
    const summary = `${totals.used} / ${totalLabel}`;
    const ariaLabel = `Kiosk licenses: ${totals.used} used of ${totals.unlimited ? "unlimited" : totals.total}`;
    const body = (
        <>
            <span className={styles.label}>Licenses</span>
            <span className={totals.over ? styles.countOver : styles.count}>{summary}</span>
        </>
    );

    return (
        <div className={styles.wrap}>
            {canOpenPage ? (
                <Link
                    to={getPagePath("licensepage")}
                    className={styles.chip}
                    aria-label={`${ariaLabel}. Open Manage Licenses`}
                >
                    {body}
                </Link>
            ) : (
                // Not a link for users without the page — still focusable so the
                // breakdown is reachable by keyboard, not hover-only.
                <span className={styles.chip} tabIndex={0} role="group" aria-label={ariaLabel}>
                    {body}
                </span>
            )}
            <div className={styles.breakdown} role="note">
                <div className={styles.breakdownHead}>Kiosk licenses</div>
                {rows.map((r) => (
                    <div key={r.LicenseType || "generic"} className={styles.breakdownRow}>
                        <span className={styles.breakdownType}>{r.LicenseType || "Generic"}</span>
                        <span className={styles.breakdownCount}>
                            {r.UsedWeight} / {r.LicensedCount == null ? "∞" : r.LicensedCount}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
